import { Card, Col, Row } from "react-bootstrap";
import { ScanMulti } from "../common/models";
import { LLMDashboardMarkdownRenderer } from "../common/LLMDashboardMarkdownRenderer";

export const SectionPalliativeDetails = (props: {
    data: any,
    title: string,
    scanMulti?: ScanMulti,
}) => {
    // Defensive check for data
    if (!props.data) {
        return (
            <Card>
                <Card.Body>
                    <Row>
                        <Col>
                            <h5>{props.title}</h5>
                        </Col>
                    </Row>
                    <Row>
                        <Col className="description-text">
                            <i>No data available</i>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>
        );
    }

    return (
        <Card>
            <Card.Body>
                <Row>
                    <Col>
                        <h5>{props.title}</h5>
                    </Col>
                </Row>
                <hr />
                
                {/* Handle items array */}
                {props.data.items && Array.isArray(props.data.items) && props.data.items.length > 0 && (
                    <Row className="mb-4">
                        <Col>
                            <div className="notes-timeline-container mt-4">
                                {props.data.items.map((item: any, index: number) => (
                                    <div key={index} className="notes-timeline-item">
                                        <div className="notes-timeline-dot" />
                                        {index !== props.data.items.length - 1 && <div className="notes-timeline-line" />}
                                        <div className="notes-timeline-content">
                                            <Row><Col><strong>{item.date || 'Date not available'}</strong></Col></Row>
                                            <Row className="text-muted">
                                                <Col>
                                                    <small>
                                                        Source: {item.citation ? (
                                                            <LLMDashboardMarkdownRenderer content={item.citation} scanMulti={props.scanMulti} />
                                                        ) : 'Source not available'}
                                                    </small>
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col className="description-text">
                                                    {item.content && (
                                                        <LLMDashboardMarkdownRenderer content={item.content} scanMulti={props.scanMulti} />
                                                    )}
                                                    {item.participants && (
                                                        <div className="mt-2">
                                                            <small className="text-muted">
                                                                <strong>Participants:</strong> {item.participants}
                                                            </small>
                                                        </div>
                                                    )}
                                                </Col>
                                            </Row>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Col>
                    </Row>
                )}

                {/* Handle additional structured data fields */}
                {props.title === "Legal" && props.data.documentation_gaps && Array.isArray(props.data.documentation_gaps) && props.data.documentation_gaps.length > 0 && (
                    <Row className="mb-3">
                        <Col>
                            <h6>Documentation Gaps:</h6>
                            <ul>
                                {props.data.documentation_gaps.map((gap: string, index: number) => (
                                    <li key={index} className="description-text">{gap}</li>
                                ))}
                            </ul>
                        </Col>
                    </Row>
                )}

                {props.title === "Prognosis" && props.data.prognostic_indicators && (
                    <Row className="mb-3">
                        <Col>
                            <h6>Prognostic Indicators:</h6>
                            <div className="description-text">
                                {props.data.prognostic_indicators.surprise_question && (
                                    <p><strong>Surprise Question:</strong> {props.data.prognostic_indicators.surprise_question}</p>
                                )}
                                {props.data.prognostic_indicators.performance_status && (
                                    <p><strong>Performance Status:</strong> {props.data.prognostic_indicators.performance_status}</p>
                                )}
                                {props.data.prognostic_indicators.admission_frequency && (
                                    <p><strong>Admission Frequency:</strong> {props.data.prognostic_indicators.admission_frequency}</p>
                                )}
                                {props.data.prognostic_indicators.organ_failure_markers && Array.isArray(props.data.prognostic_indicators.organ_failure_markers) && (
                                    <div>
                                        <strong>Organ Failure Markers:</strong>
                                        <ul>
                                            {props.data.prognostic_indicators.organ_failure_markers.map((marker: string, index: number) => (
                                                <li key={index}>{marker}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </Col>
                    </Row>
                )}

                {props.title === "Goals of Care" && props.data.current_code_status && (
                    <Row className="mb-3">
                        <Col>
                            <h6>Current Code Status:</h6>
                            <div className="description-text">
                                <p><strong>Status:</strong> {props.data.current_code_status.status || 'Unknown'}</p>
                                {props.data.current_code_status.last_documented && props.data.current_code_status.last_documented !== 'N/A' && (
                                    <p><strong>Last Documented:</strong> {props.data.current_code_status.last_documented}</p>
                                )}
                            </div>
                        </Col>
                    </Row>
                )}

                {props.title === "Communication" && (
                    <>
                        {props.data.recent_family_meetings && (
                            <Row className="mb-3">
                                <Col>
                                    <h6>Recent Family Meetings:</h6>
                                    <div className="description-text">
                                        <p><strong>Number Past Month:</strong> {props.data.recent_family_meetings.number_past_month || '0'}</p>
                                        <p><strong>Structured Communication Used:</strong> {props.data.recent_family_meetings.structured_communication_used || 'No'}</p>
                                        <p><strong>Focus:</strong> {props.data.recent_family_meetings.values_vs_procedures_focus || 'Unknown'}</p>
                                    </div>
                                </Col>
                            </Row>
                        )}
                        {props.data.family_understanding && (
                            <Row className="mb-3">
                                <Col>
                                    <h6>Family Understanding:</h6>
                                    <div className="description-text">
                                        <p><strong>Prognosis Understanding:</strong> {props.data.family_understanding.prognosis_understanding || 'Not assessed'}</p>
                                        <p><strong>Goals Alignment:</strong> {props.data.family_understanding.goals_alignment || 'Unknown'}</p>
                                    </div>
                                </Col>
                            </Row>
                        )}
                        {props.data.conflicts_present && props.data.conflicts_present !== 'No' && (
                            <Row className="mb-3">
                                <Col>
                                    <div className="alert alert-warning">
                                        <strong>Conflicts Present:</strong> {props.data.conflicts_present}
                                    </div>
                                </Col>
                            </Row>
                        )}
                    </>
                )}

                {props.title === "Critical Gaps" && (
                    <>
                        {props.data.acp_documentation && Array.isArray(props.data.acp_documentation) && props.data.acp_documentation.length > 0 && (
                            <Row className="mb-3">
                                <Col>
                                    <h6>ACP Documentation Gaps:</h6>
                                    <ul className="list-unstyled">
                                        {props.data.acp_documentation.map((gap: string, index: number) => (
                                            <li key={index} className="description-text mb-1">
                                                <i className="fas fa-exclamation-triangle text-warning me-2"></i>
                                                {gap}
                                            </li>
                                        ))}
                                    </ul>
                                </Col>
                            </Row>
                        )}
                        {props.data.clinical_needs && Array.isArray(props.data.clinical_needs) && props.data.clinical_needs.length > 0 && (
                            <Row className="mb-3">
                                <Col>
                                    <h6>Clinical Needs:</h6>
                                    <ul className="list-unstyled">
                                        {props.data.clinical_needs.map((need: string, index: number) => (
                                            <li key={index} className="description-text mb-1">
                                                <i className="fas fa-stethoscope text-primary me-2"></i>
                                                {need}
                                            </li>
                                        ))}
                                    </ul>
                                </Col>
                            </Row>
                        )}
                        {props.data.communication_needs && Array.isArray(props.data.communication_needs) && props.data.communication_needs.length > 0 && (
                            <Row className="mb-3">
                                <Col>
                                    <h6>Communication Needs:</h6>
                                    <ul className="list-unstyled">
                                        {props.data.communication_needs.map((need: string, index: number) => (
                                            <li key={index} className="description-text mb-1">
                                                <i className="fas fa-comments text-info me-2"></i>
                                                {need}
                                            </li>
                                        ))}
                                    </ul>
                                </Col>
                            </Row>
                        )}
                    </>
                )}

                {props.title === "Recommendations" && (
                    <>
                        {props.data.immediate_actions && Array.isArray(props.data.immediate_actions) && props.data.immediate_actions.length > 0 && (
                            <Row className="mb-3">
                                <Col>
                                    <h6>Immediate Actions:</h6>
                                    <ul>
                                        {props.data.immediate_actions.map((action: string, index: number) => (
                                            <li key={index} className="description-text mb-2">
                                                <LLMDashboardMarkdownRenderer content={action} scanMulti={props.scanMulti} />
                                            </li>
                                        ))}
                                    </ul>
                                </Col>
                            </Row>
                        )}
                        {props.data.care_planning && Array.isArray(props.data.care_planning) && props.data.care_planning.length > 0 && (
                            <Row className="mb-3">
                                <Col>
                                    <h6>Care Planning:</h6>
                                    <ul>
                                        {props.data.care_planning.map((plan: string, index: number) => (
                                            <li key={index} className="description-text mb-2">
                                                <LLMDashboardMarkdownRenderer content={plan} scanMulti={props.scanMulti} />
                                            </li>
                                        ))}
                                    </ul>
                                </Col>
                            </Row>
                        )}
                    </>
                )}

                {props.title === "Unmet Needs" && (
                    <>
                        {props.data.status && (
                            <Row className="mb-3">
                                <Col>
                                    <div className={`alert ${props.data.status === 'likely' ? 'alert-warning' : props.data.status === 'unlikely' ? 'alert-success' : 'alert-info'}`}>
                                        <h6>Unmet Needs Status: <strong>{props.data.status}</strong></h6>
                                    </div>
                                </Col>
                            </Row>
                        )}
                        {props.data.rationale && (
                            <Row className="mb-3">
                                <Col>
                                    <h6>Rationale:</h6>
                                    <div className="description-text">
                                        <LLMDashboardMarkdownRenderer content={props.data.rationale} scanMulti={props.scanMulti} />
                                    </div>
                                </Col>
                            </Row>
                        )}
                    </>
                )}
            </Card.Body>
        </Card>
    );
};
